import React, { useEffect, useState, useRef } from "react";
import {
    getVenues,
    createVenue,
    updateVenue,
    deleteVenue,
} from "../api/venueService";

const VenuesContent = () => {
    const [venues, setVenues] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [deleteMode, setDeleteMode] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [selectedVenue, setSelectedVenue] = useState(null); // For edit/delete
    const [form, setForm] = useState({ name: "", address: "", capacity: "", amenities: "", contact: "" });
    const [confirmName, setConfirmName] = useState("");
    const isMounted = useRef(false);

    const fetchVenues = async (limit, lastEvaluatedKey) => {
        try {
            const response = await getVenues(limit, lastEvaluatedKey);
            setVenues((prevVenues) => [...prevVenues, ...response.venues]);
            setPagination(response.pagination);
        } catch (err) {
            setError("Failed to fetch venues. Please try again later.");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!isMounted.current) {
            fetchVenues(10, null);
            isMounted.current = true;
        }
    }, []);

    const handleLoadMore = () => {
        if (pagination.lastEvaluatedKey) {
            setLoadingMore(true);
            fetchVenues(10, pagination.lastEvaluatedKey);
        }
    };

    const handleOpenModal = (venue = null, isDelete = false) => {
        setSelectedVenue(venue);
        setForm(venue || { name: "", address: "", capacity: "", amenities: "", contact: "" });
        setDeleteMode(isDelete);
        setShowModal(true);
        setConfirmName("");
        setSuccessMessage("");
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedVenue(null);
        setDeleteMode(false);
        setConfirmName("");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleConfirmNameChange = (e) => {
        setConfirmName(e.target.value);
    };

    const handleSave = async () => {
        try {
            if (selectedVenue) {
                // Update an existing venue
                await updateVenue(selectedVenue.venueId, {
                    name: form.name,
                    address: form.address,
                    capacity: form.capacity,
                    amenities: form.amenities,
                    contact: form.contact,
                });
                setVenues((prevVenues) =>
                    prevVenues.map((venue) =>
                        venue.venueId === selectedVenue.venueId ? { ...venue, ...form } : venue
                    )
                );
            } else {
                // Create a new venue
                const response = await createVenue(form);
                const newVenue = response.venue; // Extract the venue object from the response
                setVenues((prevVenues) => [newVenue, ...prevVenues]); // Add the new venue to the state
                setSuccessMessage(response.message); // Show success message from the response
            }
            handleCloseModal();
            setTimeout(() => setSuccessMessage(""), 5000); // Clear the success message after 5 seconds
        } catch (err) {
            alert("Failed to save venue. Please try again.");
        }
    };

    const handleDelete = async () => {
        if (confirmName !== selectedVenue.name) {
            alert("Entered name does not match. Please try again.");
            return;
        }

        try {
            await deleteVenue(selectedVenue.venueId);
            setVenues((prevVenues) =>
                prevVenues.filter((venue) => venue.venueId !== selectedVenue.venueId)
            );
            setSuccessMessage("Venue deleted successfully.");
            handleCloseModal();
            setTimeout(() => setSuccessMessage(""), 5000); // Clear success message after 5 seconds
        } catch (err) {
            alert("Failed to delete venue. Please try again.");
        }
    };

    return (
        <div className="venues-content">
            <h2 className="text-2xl font-bold mb-4">Venues</h2>
            <p>View detailed information for venues.</p>

            {/* Success Message */}
            {successMessage && (
                <div className="alert alert-success">{successMessage}</div>
            )}

            {/* Conditionally Render the Button */}
            {!loading && (
                <button
                    className="btn btn-success mb-3"
                    onClick={() => handleOpenModal()}
                >
                    Create New Venue
                </button>
            )}

            {loading ? (
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <div>
                    <h3>List of Venues</h3>
                    {venues.length > 0 ? (
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>Capacity</th>
                                    <th>Amenities</th>
                                    <th>Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {venues.map((venue, index) => (
                                    <tr key={venue.venueId}>
                                        <td>{index + 1}</td>
                                        <td>{venue.name}</td>
                                        <td>{venue.address}</td>
                                        <td>{venue.capacity}</td>
                                        <td>{venue.amenities}</td>
                                        <td>{venue.contact}</td>
                                        <td>
                                            <button
                                                className="btn btn-warning btn-sm me-2"
                                                onClick={() => handleOpenModal(venue)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleOpenModal(venue, true)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No venues found.</p>
                    )}
                    {pagination.lastEvaluatedKey && (
                        <button
                            className="btn btn-primary mt-3"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? "Loading..." : "Load More"}
                        </button>
                    )}
                </div>
            )}

            {/* Modal for Create/Edit/Delete */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {deleteMode
                                        ? "Confirm Venue Deletion"
                                        : selectedVenue
                                            ? "Edit Venue"
                                            : "Create New Venue"}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseModal}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {deleteMode ? (
                                    <div>
                                        <p>
                                            Are you sure you want to delete the venue <strong>{selectedVenue.name}</strong>? Please confirm its name.
                                        </p>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter venue's name"
                                            value={confirmName}
                                            onChange={handleConfirmNameChange}
                                        />
                                    </div>
                                ) : (
                                    <form>
                                        <div className="mb-3">
                                            <label className="form-label">Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Address</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="address"
                                                value={form.address}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Capacity</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="capacity"
                                                value={form.capacity}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Amenities</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="amenities"
                                                value={form.amenities}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Contact</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="contact"
                                                value={form.contact}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </form>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                {deleteMode ? (
                                    <button className="btn btn-danger" onClick={handleDelete}>
                                        Confirm Delete
                                    </button>
                                ) : (
                                    <button className="btn btn-primary" onClick={handleSave}>
                                        Save
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VenuesContent;
